import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Entity } from '../../../classes/entity.class';
import { PlanningHasEntity } from '../../../classes/planning-has-entity.class';
import { Planning } from '../../../classes/planning.class';
import { CodaltComponent } from '../../../codalt.component';
import { AuthorisationService } from '../../../services/auth/authorisation.service';
import { Features } from '../../../services/auth/features';
import { EntityType } from '../../../services/entities/entity-type.class';
import { PlanningService } from '../../../services/planning/planning.service';
import { Utils } from '../../../utils.class';
import { TracklinePlanningEditDialogComponent } from '../trackline-planning-edit-dialog/trackline-planning-edit-dialog.component';
import { TracklinePlanningEntityTypeDialogComponent } from '../trackline-planning-entity-type-dialog/trackline-planning-entity-type-dialog.component';

export interface YearPlanningItem {
    planning: Planning;
    planningHas: PlanningHasEntity;
}

export interface YearPlanningDay {
    date: Date;
    items: Map<number, YearPlanningItem[]>;
}

export interface YearPlanningWeek {
    weekNumber: number;
    year: number;
    days: YearPlanningDay[];
}

@Component({
    selector: 'app-trackline-year-planning',
    standalone: false,
    templateUrl: './trackline-year-planning.component.html',
    styleUrl: './trackline-year-planning.component.scss',
})
export class TracklineYearPlanningComponent extends CodaltComponent implements OnInit {
    readOnly = !AuthorisationService.hasFeature(Features.PlanningSaveSave);

    fcEntityType = new FormControl<string>(null);

    weeks: YearPlanningWeek[] = [];
    entities: Entity[] = [];
    loading = true;
    yearPlanningEntityTypes: EntityType[] = [];

    private fromDate: Date;
    private toDate: Date;
    private planningList: Planning[] = [];

    constructor(
        private dialog: MatDialog,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private planningService: PlanningService
    ) {
        super();
    }

    ngOnInit(): void {
        const planningKeys = new Set(this.environment.planningEntityTypes?.map(et => et.key) ?? []);
        this.yearPlanningEntityTypes = Object.values(this.environment.entitytypes)
            .filter(et => !planningKeys.has(et.key))
            .sort((a, b) => a.order - b.order);
        this.fcEntityType.setValue(this.activatedRoute.snapshot.params['entitytype'] ?? 'blast_truck');

        this.fromDate = this.getMonday(new Date());
        this.toDate = new Date(this.fromDate);
        this.toDate.setDate(this.toDate.getDate() + 7 * 52);
        Utils.setTime(this.toDate, 23, 59);

        this.subscriptions.add(
            this.planningService.getFilteredList(this.fromDate, this.toDate).subscribe(list => {
                this.planningList = list;
                this.loading = false;
                this.buildView();
            })
        );

        this.subscriptions.add(
            this.fcEntityType.valueChanges.subscribe(entitytypeKey => {
                this.buildView();
                this.router.navigate(['jaarplanning', { entitytype: entitytypeKey }], { replaceUrl: true });
            })
        );
    }

    openPlanning(planning: Planning) {
        this.subscriptions.add(
            this.dialog
                .open(TracklinePlanningEditDialogComponent, {
                    disableClose: true,
                    panelClass: 'planning-edit-dialog',
                    data: { planning },
                })
                .afterClosed()
                .subscribe(() => {})
        );
    }

    newPlanning(day: YearPlanningDay, entity: Entity) {
        const noOtherPlannings = Array.from(day.items?.values()).some(items =>
            items.some(i => i.planningHas.entity_id === entity.id)
        );
        if (!this.readOnly && !noOtherPlannings) {
            if (this.readOnly) {
                return;
            }
            const beginDate = new Date(day.date);
            Utils.setTime(beginDate, 7, 0);
            const endDate = new Date(day.date);
            Utils.setTime(endDate, 15, 30);

            this.subscriptions.add(
                this.dialog
                    .open(TracklinePlanningEntityTypeDialogComponent)
                    .afterClosed()
                    .subscribe(mainEntity => {
                        if (mainEntity) {
                            this.dialog.open(TracklinePlanningEditDialogComponent, {
                                disableClose: true,
                                panelClass: 'planning-edit-dialog',
                                data: { beginDate, endDate, mainEntity, otherEntities: [entity] },
                            });
                        }
                    })
            );
        }
    }

    getItemsForEntity(day: YearPlanningDay, entityId: number): YearPlanningItem[] {
        return day.items.get(entityId) ?? [];
    }

    private buildView(): void {
        const selectedType = this.fcEntityType.value;
        if (!selectedType) {
            this.weeks = [];
            this.entities = [];
            return;
        }

        // Flatten planning_has items filtered by their own entitytype_key
        const flatItems: YearPlanningItem[] = [];
        const entityMap = new Map<number, Entity>();

        for (const planning of this.planningList) {
            if (!planning.planning_has?.length) {
                continue;
            }
            for (const phe of planning.planning_has) {
                if (!phe.entity_id || !phe.begindate || phe.entitytype_key !== selectedType) {
                    continue;
                }
                flatItems.push({ planning, planningHas: phe });
                if (phe.entity && !entityMap.has(phe.entity_id)) {
                    entityMap.set(phe.entity_id, phe.entity);
                }
            }
        }

        // Sort entities by name
        this.entities = Array.from(entityMap.values()).sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

        // Pre-generate all weeks and days in the range
        const weekMap = new Map<string, YearPlanningWeek>();
        const cursor = new Date(this.fromDate);
        while (cursor <= this.toDate) {
            const dateOnly = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate());
            const weekNumber = Utils.getWeek(dateOnly);
            const year = this.getISOWeekYear(dateOnly);
            const weekKey = `${year}-${weekNumber}`;

            if (!weekMap.has(weekKey)) {
                weekMap.set(weekKey, { weekNumber, year, days: [] });
            }
            const week = weekMap.get(weekKey);
            const dateStr = dateOnly.toDateString();
            if (!week.days.find(d => d.date.toDateString() === dateStr)) {
                week.days.push({ date: dateOnly, items: new Map<number, YearPlanningItem[]>() });
            }
            cursor.setDate(cursor.getDate() + 1);
        }

        // Fill in planning items
        for (const item of flatItems) {
            const date = new Date(item.planningHas.begindate);
            const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            const weekNumber = Utils.getWeek(dateOnly);
            const year = this.getISOWeekYear(dateOnly);
            const weekKey = `${year}-${weekNumber}`;
            const week = weekMap.get(weekKey);
            if (!week) {
                continue;
            }
            const day = week.days.find(d => d.date.toDateString() === dateOnly.toDateString());
            if (!day) {
                continue;
            }
            const entityId = item.planningHas.entity_id;
            if (!day.items.has(entityId)) {
                day.items.set(entityId, []);
            }
            day.items.get(entityId).push(item);
        }

        // Sort weeks
        this.weeks = Array.from(weekMap.values()).sort((a, b) => {
            if (a.year !== b.year) {
                return a.year - b.year;
            }
            return a.weekNumber - b.weekNumber;
        });
    }

    private getMonday(date: Date): Date {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        d.setDate(diff);
        Utils.setTime(d, 0, 0);
        return d;
    }

    private getISOWeekYear(date: Date): number {
        const d = new Date(date.getTime());
        d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
        return d.getFullYear();
    }
}
